import React from 'react'

import { useUncontrolled } from 'hooks'

import { FormControl, MenuItem, Select as MuiSelect, Typography, type SelectChangeEvent, Stack, FormHelperText, Checkbox, Avatar, alpha } from '@mui/material'

import { ChevronDownIcon } from 'lucide-react'
import { navHover, navSelected, fieldAccentColor, fieldBorderColor, FIELD_HEIGHT, MENU_PAPER_SX, MENU_ITEM_SX } from '@repo/ui'

export interface SelectProps {
  open?: boolean
  label?: string
  /**
   * Data-driven options. When omitted (and `children` is provided instead), the
   * raw `<MenuItem>` children are rendered directly — used by the Sort toolbar.
   */
  options?: SelectItem[]
  helperText?: string
  value?: string | string[]
  defaultValue?: string | string[]
  onChange?: (value: string | string[]) => void
  variant?: 'filled' | 'standard' | 'outlined'
  fullWidth?: boolean
  disabled?: boolean
  placeholder?: string
  labelVariant?: 'body1' | 'body2' | 'body3' | 'body4'
  multiple?: boolean
  renderValue?: (value: string | string[]) => React.ReactNode
  /**
   * Raw `<MenuItem>` children. When provided (and `options` is not), these are
   * rendered instead of mapping `options` — for callers that build their own
   * items (e.g. the Sort column/direction pickers).
   */
  children?: React.ReactNode
  /**
   * Control the trigger footprint:
   *  - `'field'` (default) — the FIELD_HEIGHT (49px) form-field look (labelled form selects).
   *  - `'compact'` — an inline toolbar control: no field-height min-height, tighter
   *    padding, `primary[50]` bg, 8px radius, and a brand[500] hover border.
   *    Used by the Sort toolbar (formerly `Select2`).
   */
  size?: 'field' | 'compact'
  /**
   * Style overrides merged onto the underlying control, AFTER the size preset — so a
   * caller can widen or otherwise adjust one instance without forking the component.
   *
   * Added because it was silently missing: passing `sx` was accepted by JSX and then
   * dropped on the floor, so a `minWidth` meant to stop a long option label running under
   * the dropdown chevron did nothing at all, with no error to explain why.
   */
  sx?: Record<string, any>
}

export type SelectItem = {
  label: string
  value: string
  /**
   * Image URL for the option's leading avatar. Declaring the key at all — even as null/'' —
   * opts the option into an avatar SLOT: a logo-less entry then falls back to its initial
   * rather than rendering nothing. Omit the key entirely for options that have no avatar.
   *
   * The distinction matters because `avatar && <Avatar/>` silently produced ragged lists:
   * in a company picker, companies WITH a logo got a 32px avatar and companies without got
   * no leading element at all, so their labels sat at a different x than their neighbours'.
   */
  avatar?: string | null
  /**
   * Caller-supplied leading element, taking precedence over `avatar`. Exists so an option can
   * use the DISPLAY component its entity already has everywhere else — `CompanyAvatar` for a
   * company (rounded-square, logo plate, initials fallback), `Avatar` for a person (circular).
   * A generic input component can't know which shape an option is, and MUI's default circular
   * person-glyph is wrong for an organization.
   */
  adornment?: React.ReactNode
  /** Optional right-aligned badge/label (e.g. "Default" / "Custom"). */
  rightLabel?: string
}

const ITEM_HEIGHT = 40
const ITEM_PADDING_TOP = 8
const ITEMS_TO_SHOW = 8

const MenuProps = {
  // The trigger→menu gap is NOT set here. It used to be — `sx: { mt: '8px' }` on the
  // Menu ROOT, which shifts the fixed-position Popover container and so moves the paper
  // with it. That is a second, independent lever on the same distance as the paper's own
  // `mt`, and the two ADD: once `MENU_PAPER_SX` (spread onto the paper below) carried the
  // 8px, this root margin made the gap 16. One gap, one source — the paper's.
  autoFocus: false,
  PaperProps: {
    sx: (theme: any) => ({
      maxHeight: ITEM_HEIGHT * ITEMS_TO_SHOW + ITEM_PADDING_TOP,
      // Match the design-system dropdowns (MenuButton/Combobox): 8px radius + flat
      // dark surface. The theme MuiMenu override already handles border/shadow.
      borderRadius: '8px',
      backgroundImage: 'none',
      // House menu padding convention (MENU_PAPER_SX) — was a tight uniform 8px
      // inset (the committed Select2 dropdown fix, 074beeb4) that read as more
      // compressed than every other menu in the app.
      ...MENU_PAPER_SX,
      minWidth: '240px',
      // shadows[3], matching every other popover (see Sort).
      boxShadow: theme.shadows[3],
      // Clip item highlights to the rounded corners so the selected/hover row
      // never bleeds past the popover edge.
      overflow: 'hidden',
    }),
  },
  // Item treatment for raw-`children` callers (Sort) whose `<MenuItem>`s carry no
  // per-item sx. Matches the committed Select2 dropdown: rounded 6px rows, 8/12
  // padding, 4px gap, subtle brand-tint hover/selected. The `options`-mapped items
  // carry their own sx and are unaffected by these MuiMenuItem-root rules.
  MenuListProps: {
    sx: (theme: any) => ({
      pb: 0,
      pt: 0,
      borderRadius: 2,
      '& .MuiMenuItem-root': {
        alignItems: 'center',
        px: 3,
        py: 2,
        borderRadius: 1.5,
        mb: 1,
        '&:last-of-type': { mb: 0 },
        // navChrome. Was brand[600] @0.12 for hover AND selected:hover — a different SHADE
        // (brand[600] #7F48DF vs the token's brand[500] #8F68EA) as well as a different alpha, so
        // this dropdown's purple was visibly not the purple of the menus around it.
        '&:hover, &.Mui-focusVisible': {
          backgroundColor: navHover(theme),
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: navSelected(theme),
        },
      },
    }),
  },
}

export const Select = ({
  open,
  options,
  helperText,
  onChange,
  defaultValue,
  value,
  label,
  variant = 'outlined',
  disabled = false,
  fullWidth = true,
  placeholder,
  labelVariant = 'body1',
  multiple = false,
  renderValue,
  children,
  size = 'field',
  sx: sxOverride,
}: SelectProps) => {
  const normalizedDefaultValue: string | string[] =
    defaultValue !== undefined ? defaultValue : multiple ? [] : ''

  const [_value, setValue] = useUncontrolled<string | string[]>({
    value,
    defaultValue: normalizedDefaultValue,
    onChange,
  })

  const [_open, setOpen] = useUncontrolled({
    value: open,
    defaultValue: false,
  })

  const internalOnChange = (event: SelectChangeEvent<string | string[]>) => {
    setValue(event?.target?.value as string | string[])
  }

  const isCompact = size === 'compact'

  const selectEl = (
    <MuiSelect
      open={_open}
      value={_value}
      multiple={multiple}
      variant={variant}
      placeholder={placeholder}
      displayEmpty={Boolean(placeholder)}
      IconComponent={() => (
        <ChevronDownIcon
          strokeWidth={1}
          style={{
            position: 'absolute',
            right: 12,
            pointerEvents: 'none',
            transform: _open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
            width: 20,
            height: 20,
          }}
        />
      )}
      onChange={internalOnChange}
      MenuProps={MenuProps}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      sx={(theme) => ({
        ...(isCompact
          ? {
              // Compact inline control, and now actually TERTIARY — the old comment claimed
              // "the ex-Select2 tertiary look" while the code painted a primary[50] fill, i.e. a
              // contained control. It sits inside a popover that is already a raised surface, so a
              // filled control on top of that reads as a second panel stacked on the first.
              // Transparent + a border inherits whatever surface it is placed on.
              //
              // WIDTH sizes to content. It was pinned at MUI's default (measured 117px), which
              // CLIPPED its own label — "Newest first" overran with the chevron sitting on the last
              // character. The values here are data-driven (any column name can appear), so no
              // fixed width can be right; a sort control whose label is truncated cannot tell you
              // what it is sorting by.
              fontSize: 16,
              width: 'fit-content',
              minWidth: 0,
              // Tertiary BUTTON, not a field: a grey fill and NO border. The border was added when
              // this was treated as a field (it shares fieldChrome elsewhere), but inside a popover
              // these read as buttons with a chevron — and a fill plus an edge on a control that
              // sits on an already-raised surface is one definition too many. The fill alone marks
              // the hit area.
              backgroundColor: theme.palette.mode === 'light' ? '#f6f6f6' : theme.palette.primary?.[50],
              borderRadius: '8px',
              // fieldChrome, not primary[50]. This sat one whole step FAINTER than every
              // other field's rest border (primary[50] is a fill token, not an edge token),
              // so a Select read as barely-bordered beside the Input next to it.
              border: '1px solid transparent',
              transition: 'border-color 0.15s ease-in-out, background 0.15s ease-in-out',
              '& .MuiOutlinedInput-notchedOutline, & fieldset': {
                border: 'none !important',
              },
              '&:hover': {
                // brand[600], via fieldChrome — was brand[500], a different accent from the
                // hover/focus colour every other field uses (§31: hover and focus are the
                // same brand colour, and it is the same one everywhere).
                // Border stays transparent so the box never changes size (the same transition
                // artifact the toolbar buttons hit); hover reads through the FILL instead.
                border: '1px solid transparent',
                backgroundColor: theme.palette.mode === 'light' ? '#ededed' : theme.palette.primary?.[100],
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused fieldset': {
                border: 'none !important',
              },
              '& .MuiSelect-select': {
                // Asymmetric on purpose. MUI positions `.MuiSelect-icon` ABSOLUTELY at the
                // right edge, so it does not reserve any space — with a symmetric 12px the
                // value text ran straight under the chevron on any label long enough to
                // fill the control ("Oldest first"). Widening the control does not help,
                // because the chevron moves with the right edge. The `field` size already
                // reserved 40px for exactly this; compact never did, so EVERY compact
                // select had the overlap latent.
                pl: '12px',
                pr: '32px',
                py: '6px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '16px',
                backgroundColor: 'transparent',
              },
            }
          : {
              // VRG-705 — FIELD_HEIGHT (49), the shared Input's height; this was 47, 2px
              // short of the TextField beside it (e.g. the role picker in Invite user).
              height: `${FIELD_HEIGHT}px`,
              // The design-system field treatment (working-conventions §31), sourced from the
              // same tokens as the @repo/ui `Input`: 8px radius, a real 1px border at rest,
              // brand[600] on hover AND focus, and a soft 3px brand ring on focus — the border
              // WIDTH never changes, so focus causes no layout shift.
              //
              // This replaces a bespoke look that was borderless at rest with a hard
              // `0 0 0 1.5px #7F48DF` ring on focus. Borderless made the control read as a
              // filled block rather than a field, so it did not sit with the TextFields,
              // DatePickers and RichText editors beside it — which is the whole point of §31
              // being a single source.
              backgroundColor:
                theme.palette.mode === 'light'
                  ? '#ffffff'
                  : theme.palette.primary?.[50] || '#f6f6f6',
              borderRadius: '8px',
              border: `1px solid ${
                theme.palette.mode === 'light'
                  ? theme.palette.grey?.[300] || '#acafb4'
                  : theme.palette.primary?.[100] || '#e7e7e7'
              }`,
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              // MUI's own notched outline is suppressed — the border above is the one that
              // renders, so the two can't double up.
              '& .MuiOutlinedInput-notchedOutline, & fieldset': {
                border: 'none !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused fieldset': {
                border: 'none !important',
              },
              '&:hover': {
                borderColor: theme.palette.brand?.[600] || '#7F48DF',
              },
              '&.Mui-focused, &:focus-within': {
                borderColor: theme.palette.brand?.[600] || '#7F48DF',
                boxShadow: `0 0 0 3px ${
                  theme.palette.brand?.['600-6%'] || 'rgba(127, 72, 223, 0.16)'
                }`,
              },
              '& .css-1xx1iwo-MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root:hover': {
                border: 'none !important',
              },
              '& .MuiSelect-select': {
                paddingRight: '40px !important',
                paddingLeft: '11px !important',
                paddingTop: '11px !important',
                paddingBottom: '11px !important',
                height: `${FIELD_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
                // Transparent, not a repeat of the fill: the ROOT owns the background now
                // (white in light mode). Re-declaring primary[50] here would paint the old
                // grey block back over it and leave only a 1px white frame.
                backgroundColor: 'transparent',
                boxSizing: 'border-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '16px',
              },
              '& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                borderRadius: '8px',
                '&:hover': {
                  border: '1px solid #7F48DF',
                },
                '&:focus': {
                  borderRadius: '8px',
                },
              },
              '& .MuiOutlinedInput-root': {
                height: `${FIELD_HEIGHT}px`,
                backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                borderRadius: '8px',
                '& fieldset': {
                  border: 'none !important',
                },
                '&:hover fieldset': {
                  border: 'none !important',
                },
                '&.Mui-focused fieldset': {
                  border: 'none !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
              },
              '& .css-r01yff-MuiNotchedOutlined-root-MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
              '& .MuiFilledInput-root': {
                height: `${FIELD_HEIGHT}px`,
                backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                borderRadius: '8px',
                '&:hover': {
                  border: '1px solid #7F48DF',
                },
                '&:before': {
                  border: 'none',
                },
                '&:after': {
                  border: 'none',
                },
                '&:hover:not(.Mui-disabled):before': {
                  border: 'none',
                },
              },
              '& .MuiInput-root': {
                height: `${FIELD_HEIGHT}px`,
                backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                borderRadius: '8px',
                '&:hover': {
                  border: '1px solid #7F48DF',
                },
                '&:before': {
                  border: 'none',
                },
                '&:after': {
                  border: 'none',
                },
                '&:hover:not(.Mui-disabled):before': {
                  border: 'none',
                },
              },
              ...(variant === 'filled' && {
                '& .MuiFilledInput-root': {
                  backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                    border: '1px solid #7F48DF',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
                  },
                },
              }),
            }),
        // Merged LAST so a caller's override wins over the size preset.
        ...(sxOverride ?? {}),
      })}
      renderValue={renderValue}
    >
      {children
        ? children
        : (options ?? []).map(item => {
            const isSelected = Array.isArray(_value)
              ? _value.includes(item.value)
              : _value === item.value

            return (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={(theme) => ({
                  minHeight: '48px',
                  // House item padding convention (MENU_ITEM_SX) — was a tighter
                  // 12px that read as compressed next to every other menu.
                  ...MENU_ITEM_SX,
                  borderRadius: '8px',
                  // Hover = neutral, selected = brand, both from navChrome. The intent in the
                  // old comment was right; the VALUE was a local copy at brand[600] @0.16 and
                  // ended up brighter than every other menu once the shared token moved.
                  '&.Mui-selected, &.Mui-selected:hover': {
                    backgroundColor: navSelected(theme),
                  },
                })}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                    {multiple && (
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        sx={{ p: 0 }}
                      />
                    )}
                    {item.adornment ??
                      ('avatar' in item ? (
                        // `src={undefined}` + `alt` makes MUI fall back to the label's initial,
                        // so every option in an avatar-bearing list keeps the same leading slot
                        // and the labels stay aligned. See SelectItem['avatar'].
                        <Avatar
                          src={item.avatar || undefined}
                          alt={item.label}
                          sx={{ width: 32, height: 32, fontSize: 13 }}
                        />
                      ) : null)}
                    <Typography variant="body2" noWrap>{item.label}</Typography>
                  </Stack>
                  {item.rightLabel && (
                    <Typography variant="body3" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                      {item.rightLabel}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            )
          })}
    </MuiSelect>
  )

  return (
    <FormControl
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled}
      // The WRAPPER has to size to content too, not just the control inside it. Measured: the
      // FormControl stayed pinned at 100px (fullWidth inside a flex row) while the inner control
      // sized to its label at 121px and 79px — so the first control overflowed its own wrapper by
      // 21px and the second, positioned after the 100px wrapper plus the Stack's 12px margin,
      // started 9px UNDERNEATH it. The two chips overlapped by 9px.
      //
      // The Stack's spacing is applied to this element, so once it matches the control's width the
      // 12px gap becomes real. This is why setting `width: fit-content` on the control alone looked
      // like it had done nothing.
      sx={isCompact ? { width: 'fit-content', flexShrink: 0 } : undefined}
    >
      {(label || helperText) && (
        <Stack mb={2} direction="row" spacing={2}>
          {label && (
            <Typography variant={labelVariant}>
              {label}
            </Typography>
          )}
          {helperText && <FormHelperText>({helperText})</FormHelperText>}
        </Stack>
      )}

      {selectEl}
    </FormControl>
  )
}
